import {
  ArrowsRightLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  PrinterIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  PlannerColourBy,
  PlannerComplexityFilter,
  PlannerDayRange,
  PlannerSort,
  PlannerView,
} from "../../models/ProductionPlanner";
import classNames from "../../utils/classNames";
import Tooltip from "../shared/Tooltip";

interface PlannerToolbarProps {
  view: PlannerView;
  setView: (view: PlannerView) => void;
  sort: PlannerSort;
  setSort: (sort: PlannerSort) => void;
  complexityFilter: PlannerComplexityFilter;
  setComplexityFilter: (filter: PlannerComplexityFilter) => void;
  colourBy: PlannerColourBy;
  setColourBy: (colourBy: PlannerColourBy) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dayRange: PlannerDayRange;
  setDayRange: (range: PlannerDayRange) => void;
  selectedDate: DateTime;
  setSelectedDate: (date: DateTime) => void;
  bayCount: number;
  onPrintAll: () => void;
  isPrintingLabels: boolean;
  onClearAll: () => void;
  isClearing: boolean;
}

const sortOptions: { value: PlannerSort, label: string }[] = [
  { value: "Oldest", label: "Oldest first" },
  { value: "Newest", label: "Newest first" },
  { value: "Easiest", label: "Easiest first" },
  { value: "Hardest", label: "Hardest first" },
];

const complexityOptions: PlannerComplexityFilter[] = ["All", "Easy", "Medium", "Hard"];

const dayRangeOptions: PlannerDayRange[] = [1, 3, 5, 7];

const labelClass = "block text-[11px] uppercase tracking-wide text-gray-500 mb-0.5";
const selectClass = "text-sm border border-gray-300 rounded px-2 pr-5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";

export default function PlannerToolbar({
  view,
  setView,
  sort,
  setSort,
  complexityFilter,
  setComplexityFilter,
  colourBy,
  setColourBy,
  searchTerm,
  setSearchTerm,
  dayRange,
  setDayRange,
  selectedDate,
  setSelectedDate,
  bayCount,
  onPrintAll,
  isPrintingLabels,
  onClearAll,
  isClearing,
}: PlannerToolbarProps) {
  const { t } = useTranslation("production");
  const today = DateTime.local().startOf("day");
  const isToday = selectedDate.hasSame(today, "day");

  const rangeLabel =
    view === "Bays"
      ? selectedDate.toFormat("ccc d LLL")
      : `${selectedDate.toFormat("d LLL")} - ${selectedDate.plus({ days: dayRange - 1 }).toFormat("d LLL")}`;

  const handleDateInput = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (parsed.isValid) {
      setSelectedDate(parsed.startOf("day"));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label className={labelClass}>{t("Sort")}</label>
        <select
          className={selectClass}
          value={sort}
          onChange={(e) => setSort(e.target.value as PlannerSort)}
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.label)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass}>{t("Complexity")}</label>
        <select
          className={selectClass}
          value={complexityFilter}
          onChange={(e) => setComplexityFilter(e.target.value as PlannerComplexityFilter)}
        >
          {complexityOptions.map((c) => (
            <option key={c} value={c}>
              {t(c)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass}>{t("Colour by")}</label>
        <div className="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setColourBy("Complexity")}
            className={classNames(
              "px-2 py-1",
              colourBy === "Complexity"
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            )}
          >
            {t("Complexity")}
          </button>
          <button
            type="button"
            onClick={() => setColourBy("Age")}
            className={classNames(
              "px-2 py-1 border-l border-gray-300",
              colourBy === "Age"
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            )}
          >
            {t("Age")}
          </button>
        </div>
      </div>

      <div className="flex-1 min-w-[200px]">
        <label className={labelClass}>{t("Search")}</label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className={classNames(selectClass, "w-full pl-7")}
            placeholder={t("VRM or Make / Model")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>{t("View")}</label>
        <div className="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setView("ProductionLine")}
            className={classNames(
              "px-2 py-1",
              view === "ProductionLine"
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            )}
          >
            {t("Production Line")}
          </button>
          <button
            type="button"
            onClick={() => setView("Bays")}
            className={classNames(
              "px-2 py-1 border-l border-gray-300",
              view === "Bays"
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            )}
          >
            {t("Bays")}
          </button>
        </div>
      </div>

      {view === "Bays" ? (
        <div>
          <label className={labelClass}>{t("Bays")}</label>
          <div className="text-sm px-2 py-1 text-gray-700">
            {bayCount === 1
              ? t("{{count}} bay", { count: bayCount })
              : t("{{count}} bays", { count: bayCount })}
          </div>
        </div>
      ) : (
        <div>
          <label className={labelClass}>{t("Planner days")}</label>
          <div className="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
            {dayRangeOptions.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDayRange(d)}
                className={classNames(
                  "px-2 py-1 border-l border-gray-300 first:border-l-0",
                  dayRange === d
                    ? "bg-primary-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                )}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ml-auto">
        <label className={labelClass}>{t("Date")}</label>
        <div className="inline-flex items-center gap-x-1">
          <button
            type="button"
            onClick={() => setSelectedDate(selectedDate.minus({ days: 1 }))}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            title={t("Previous day")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(today)}
            disabled={isToday}
            className={classNames(
              "text-xs px-2 py-1 rounded border",
              isToday
                ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            )}
          >
            {t("Today")}
          </button>
          <span className="text-sm text-gray-700 px-2 min-w-[120px] text-center">
            {rangeLabel}
          </span>
          <button
            type="button"
            onClick={() => setSelectedDate(selectedDate.plus({ days: 1 }))}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            title={t("Next day")}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <input
            type="date"
            value={selectedDate.toISODate() ?? ""}
            onChange={(e) => handleDateInput(e.target.value)}
            className={selectClass}
          />
        </div>
      </div>

      <div className="flex items-end gap-x-2">
        <button
          type="button"
          onClick={onPrintAll}
          disabled={isPrintingLabels}
          className="inline-flex items-center gap-x-1 text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <PrinterIcon className="h-4 w-4" />
          {isPrintingLabels ? t("Printing...") : t("Print All")}
        </button>
        <button
          type="button"
          onClick={onClearAll}
          disabled={isClearing}
          className="inline-flex items-center gap-x-1 text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <TrashIcon className="h-4 w-4" />
          {t("Clear")}
        </button>
        {/* <Tooltip content="Coming soon">
          <button
            type="button"
            disabled
            className="inline-flex items-center gap-x-1 text-sm rounded px-2 py-1 bg-gray-800 text-white opacity-60 cursor-not-allowed"
          >
            <SparklesIcon className="h-4 w-4" />
            Auto Allocate
          </button>
        </Tooltip> */}
        <span className="hidden">
          <ArrowsRightLeftIcon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}
